import React, {
  FC,
  memo,
  useCallback,
  useRef,
  type MutableRefObject,
} from 'react';
import { useTranslation } from 'next-i18next';
import { MecImage } from '@adapters/bigcommerce/types/product';
import { getGalleryImageTagDisplay } from '@utils/getGalleryImageTagDisplay';
import CanadianBadge from '_components/UI/CanadianBadge';
import { ActivatableImageContainer } from '../ImageContainer';
import {
  FIRST_IMAGE_SIZES,
  OTHER_IMAGE_SIZES,
  GalleryImageFitMode,
  getGalleryCanvasAspectRatio,
} from '../constants';
import {
  createInitialGalleryPointerDragSession,
  useGalleryPointerWindowListeners,
  type GalleryPointerDragSession,
} from '../galleryPointerDrag';
import styles from '../ImageGallery.module.scss';

interface GalleryTrackProps {
  images: MecImage[];
  /** Active PDP swatch name (matches `MecImage.swatchMatch`). */
  colourFilterName?: string | null;
  isMadeInCanada: boolean;
  isCanadianBrand: boolean;
  isNarrowViewport: boolean;
  imageFitMode?: GalleryImageFitMode;
  coverRangeOverride?: number;
  carouselRef: React.RefObject<HTMLDivElement | null>;
  onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  onClickCapture: (event: React.MouseEvent<HTMLDivElement>) => void;
  onImageActivate: (
    index: number,
    options: {
      trigger?: HTMLButtonElement | null;
      source: 'keyboard' | 'mouse' | 'touch';
    }
  ) => void;
  /**
   * Optional non-interactive node rendered as the first cell in the grid (e.g. a colour
   * placeholder tile). When present, no image cell is treated as `isFirst`, so all images
   * render at secondary size and none triggers the hero layout slot.
   */
  leadingNode?: React.ReactNode;
}

interface GalleryImageCellProps {
  image: MecImage;
  index: number;
  isFirst: boolean;
  isMadeInCanada: boolean;
  isCanadianBrand: boolean;
  isNarrowViewport: boolean;
  imageFitMode?: GalleryImageFitMode;
  coverRangeOverride?: number;
  tagDisplay?: string;
  activateAriaLabel: string;
  pointerDragSessionRef: MutableRefObject<GalleryPointerDragSession>;
  onImageActivate: GalleryTrackProps['onImageActivate'];
}

const GalleryImageCell = memo(function GalleryImageCell({
  image,
  index,
  isFirst,
  isMadeInCanada,
  isCanadianBrand,
  isNarrowViewport,
  imageFitMode,
  coverRangeOverride,
  tagDisplay,
  activateAriaLabel,
  pointerDragSessionRef,
  onImageActivate,
}: GalleryImageCellProps) {
  const handleActivate = useCallback(
    (options: {
      trigger?: HTMLButtonElement | null;
      source: 'keyboard' | 'mouse' | 'touch';
    }) => {
      onImageActivate(index, options);
    },
    [index, onImageActivate]
  );

  return (
    <ActivatableImageContainer
      image={image}
      isPriority={isFirst}
      sizes={isFirst ? FIRST_IMAGE_SIZES : OTHER_IMAGE_SIZES}
      canvasAspectRatio={getGalleryCanvasAspectRatio({
        isFirst,
        isNarrowViewport,
      })}
      imageFitMode={imageFitMode}
      coverRangeOverride={coverRangeOverride}
      isNarrowViewport={isNarrowViewport}
      tagDisplay={tagDisplay}
      pointerDragSessionRef={pointerDragSessionRef}
      onActivate={handleActivate}
      activateAriaLabel={activateAriaLabel}
      testId={`image-gallery-main-${index}`}
    >
      {isFirst && (isMadeInCanada || isCanadianBrand) && (
        <CanadianBadge
          className={styles.canadianBadge}
          variant={isMadeInCanada ? 'madeInCanada' : 'canadianBrand'}
        />
      )}
    </ActivatableImageContainer>
  );
});

const GalleryTrack: FC<GalleryTrackProps> = ({
  images,
  colourFilterName = null,
  isMadeInCanada,
  isCanadianBrand,
  isNarrowViewport,
  imageFitMode,
  coverRangeOverride,
  carouselRef,
  onPointerDown,
  onClickCapture,
  onImageActivate,
  leadingNode,
}) => {
  const { t } = useTranslation('pdp');
  const colourLabel = t('product.colour');
  const pointerDragSessionRef = useRef<GalleryPointerDragSession>(
    createInitialGalleryPointerDragSession()
  );

  useGalleryPointerWindowListeners(pointerDragSessionRef);

  return (
    <div
      ref={carouselRef}
      className={styles.galleryGrid}
      onPointerDown={onPointerDown}
      onClickCapture={onClickCapture}
    >
      {leadingNode}
      {images.map((image, index) => {
        // When a leadingNode occupies the hero slot, no image is first.
        const isFirst = !leadingNode && index === 0;
        const tagDisplay = getGalleryImageTagDisplay(
          image,
          colourFilterName,
          colourLabel
        );

        return (
          <GalleryImageCell
            key={`${image.urlOriginal}-${image.altText}`}
            image={image}
            index={index}
            isFirst={isFirst}
            isMadeInCanada={isMadeInCanada}
            isCanadianBrand={isCanadianBrand}
            isNarrowViewport={isNarrowViewport}
            imageFitMode={imageFitMode}
            coverRangeOverride={coverRangeOverride}
            tagDisplay={tagDisplay}
            activateAriaLabel={
              tagDisplay
                ? t('product.openImageZoomWithLabel', { label: tagDisplay })
                : t('product.openImageZoom')
            }
            pointerDragSessionRef={pointerDragSessionRef}
            onImageActivate={onImageActivate}
          />
        );
      })}
    </div>
  );
};

export default GalleryTrack;
